import { ELiteMembershipStatus } from './mutual-fund-detail.types'

export enum TIME_UNITS {
  YEAR = 'YEAR',
  MONTH = 'MONTH',
  DAY = 'DAY',
  ALL = 'ALL'
}

export enum UNITS {
  PERCENTAGE = 'PERCENTAGE',
  RUPEES = 'RUPEES',
  YEAR = TIME_UNITS['YEAR'],
  MONTH = TIME_UNITS['MONTH'],
  DAY = TIME_UNITS['DAY'],
  ALL = TIME_UNITS['ALL']
}

export enum EventNames {
  UserClicked = 'UserClicked',
  ButtonTap = 'ButtonTap',
  ScreenLoaded = 'ScreenLoaded',
  SliderMove = 'slider_move',
  KnowMoreBottomSheetLoad = 'knowmore_bottomsheet_load',
  BottomSheetLoad = 'bottomsheet_load',
  VScroll = 'vscroll',
  GraphInteraction = 'graph_interaction',
  UpdateInfoButton = 'update_info_button'
}

export type ReactQueryKey = string | string[]

export enum EliteLandingPageState {
  ONB_NOT_INITIATED = 'ONB_NOT_INITIATED',
  ONB_IN_PROGRESS = 'ONB_IN_PROGRESS',
  PAYMENT_NOT_INITIATED = 'PAYMENT_NOT_INITIATED',
  PAYMENT_PENDING = 'PAYMENT_PENDING',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  RE_INITIATE_PAYMENT = 'RE_INITIATE_PAYMENT',
  REFUND_PENDING = 'REFUND_PENDING',
  PAYMENT_COMPLETED_SUBSCRIPTION_PENDING = 'PAYMENT_COMPLETED_SUBSCRIPTION_PENDING',
  PAYMENT_COMPLETED_SUBSCRIPTION_PENDING_WARNING = 'PAYMENT_COMPLETED_SUBSCRIPTION_PENDING_WARNING',
  REFUND_DUE_TO_ESIGN_PENDING = 'REFUND_DUE_TO_ESIGN_PENDING',
  FAMILY_ONBOARDING_NOT_ALLOWED = 'FAMILY_ONBOARDING_NOT_ALLOWED',
  PLAN_EXPIRED = 'PLAN_EXPIRED',
  ELITE = 'ELITE',
  ELITE_PAYMENT_COMPLETED_PRIME_PENDING = 'ELITE_PAYMENT_COMPLETED_PRIME_PENDING'
}

export enum NavigationBarType {
  THREE_BUTTON = 'three-button',
  GESTURE = 'gesture'
}

export type EliteInsightsCta = {
  type: 'CTA'
  ctaType: string
  method: string
  navigationStyle?: string
  url?: string
  isNavigationDeferred?: boolean
  requestPayload?: string
}

export enum TaxSummaryType {
  NO_PLAN = 'NO_PLAN',
  ONLY_TAX_SAVING = 'ONLY_TAX_SAVING',
  ONLY_BOOK_GAINS = 'ONLY_BOOK_GAINS',
  BOOK_GAINS_WITH_TAX_SAVING = 'BOOK_GAINS_WITH_TAX_SAVING',
  ALL_OPTIMISED = 'ALL_OPTIMISED'
}

export type TaxSummaryBlock = {
  cta: EliteInsightsCta
  summaryType?: `${TaxSummaryType}`
  financialYear: string
  taxLiability: number
  bookGains?: number
  taxSavings?: number
}

export type RegularToDirectInsightBlock = {
  regularToDirectCtaRequest?: EliteInsightsCta
  fundsCount?: number
}

export type RegularPlanAlertBlock = {
  cta?: EliteInsightsCta
  extraCommissions?: number
}

export type EliteInsights = {
  taxSummary?: TaxSummaryBlock
  /** @deprecated Use `taxSummary` */
  taxSummaryCta?: EliteInsightsCta
  rebalanceCtaRequest: EliteInsightsCta
  timeToRebalancePlanDrop: number
  firstPlanMonth: number
  powerSelectCtaRequest: EliteInsightsCta
  eliteLensCtaRequest?: EliteInsightsCta
  regularPlanAlert?: RegularPlanAlertBlock
  regularToDirect?: RegularToDirectInsightBlock
  userElite: boolean
  rebalanceAvailable: boolean
  userEligibleForRebalance: boolean
  numberOfFundsToRebalance?: number
  numberOfFundsExcludedFromRebalance?: number
  rebalancePlanCount?: number
  rebalanceSnapshotDate?: number
}

export type EliteMoney = {
  amount: number
  unit: `${UNITS.RUPEES}`
}

export type ElitePlanTenure = {
  value: string | number
  period: `${TIME_UNITS}`
}

/**
 * `PlanDetails` from backend — current subscription plan and upgrade target share this shape
 * (aligned with `EliteOnboardingEligiblePlan` fee/tenure fields where applicable).
 */
export type EliteSubscriptionPlanDetails = {
  basePlanId: string
  effectivePlanId?: string
  basePlanFees: EliteMoney
  basePlanFeesInclGst: EliteMoney
  effectivePlanFees?: EliteMoney
  effectivePlanFeesInclGst?: EliteMoney
  basePlanTenure: ElitePlanTenure
  effectivePlanTenure?: ElitePlanTenure
  /** List / MRP ex-GST when different from effective offer price (same role as on `EliteOnboardingEligiblePlan`). */
  actualPlanPrice?: EliteMoney
  recommended?: boolean
  priority?: number
  specialOffering?: boolean
  /** Nested yearly (or other) upgrade target when returned on a plan row or inside `subscriptionPlanDetails`. */
  upgradePlanDetails?: EliteSubscriptionPlanDetails
}

export type ElitePlanOfferType =
  | 'PRICE_BASED'
  | 'TENURE_BASED'
  | 'FESTIVE_BASED'
  | 'NO_OFFER'
  | 'INDEPENDENCE_DAY_OFFER_EXTENDED_TIME'
  | 'INDEPENDENCE_DAY_OFFER_DISCOUNTED_PRICE'
  | 'SIGNUP_TENURE_BASED_OFFER'
  | 'TWELVE_PLUS_THREE'

/** GST-inclusive upgrade checkout summary from advisory. */
export type EliteSubscriptionUpgradePricingDetails = {
  upgradePlanPrice: number
  adjustmentFromCurrentPlan: number
  totalPayableAmount: number
  newPlanValidTill: number
  totalSavings?: number
  familyDiscount?: number
  offerType: ElitePlanOfferType
  amountDiff?: number
  tenureDiff?: number
  percentageSavings?: number
}

export enum EliteSubscriptionAutoPayStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

export enum EliteOnboardingRenewalStatus {
  RENEWAL_AUTOPAY_PAYMENT_FAILED_RETRYING = 'RENEWAL_AUTOPAY_PAYMENT_FAILED_RETRYING',
  RENEWAL_PAYMENT_PENDING = 'RENEWAL_PAYMENT_PENDING',
  RENEWAL_AUTOPAY_PAYMENT_FAILED = 'RENEWAL_AUTOPAY_PAYMENT_FAILED',
  RE_INITIATE_PAYMENT = 'RE_INITIATE_PAYMENT'
}

/** Upgrade block on `subscriptionDetails` (replaces legacy top-level `upgradePlanDetails` only on newer payloads). */
export type EliteSubscriptionUpgradeDetails = {
  upgradePlanDetails: EliteSubscriptionPlanDetails
  upgradeCta?: EliteInsightsCta
  upgradePricingDetails?: EliteSubscriptionUpgradePricingDetails
}

/** @deprecated Use `EliteSubscriptionPlanDetails` */
export type EliteUpgradePlanDetails = EliteSubscriptionPlanDetails

/** Active / renewal / upgrade context from onboarding advisory (v2 ELITE payload). Mirrors `EliteSubscriptionDetails` on backend. */
export type EliteSubscriptionDetails = {
  status: `${ELiteMembershipStatus}`
  autoPayStatus?: `${EliteSubscriptionAutoPayStatus}`
  /** Top-level fee when backend still sends `Money` here (legacy / some states); may be `null`. */
  membershipFees?: EliteMoney | null
  /** API / client historical field name */
  subscriptionExpiredOn?: number
  /** Java `subscriptionExpiresOn` — same epoch, alternate key from some services */
  subscriptionExpiresOn?: number
  vpa?: string
  autoPayDetails?: Record<string, unknown>
  /** Newer payloads: upgrade CTA, pricing, and target plan */
  upgradeDetails?: EliteSubscriptionUpgradeDetails
  /** Legacy: top-level upgrade target only */
  upgradePlanDetails?: EliteSubscriptionPlanDetails
  subscriptionPlanDetails?: EliteSubscriptionPlanDetails
  subscriptionEndTime?: number
  activationExpiryDate?: number
  specialOffering?: boolean
  offerStartTime?: number
  offerExpiryTime?: number
}

export type EliteOnboardingEligiblePlan = {
  recommended: boolean
  basePlanId: string
  effectivePlanId?: string
  effectivePlanFees?: {
    amount: number
    unit: `${UNITS.RUPEES}`
  }
  effectivePlanFeesInclGst?: {
    amount: number
    unit: `${UNITS.RUPEES}`
  }
  basePlanFees: {
    amount: number
    unit: `${UNITS.RUPEES}`
  }
  basePlanFeesInclGst: {
    amount: number
    unit: `${UNITS.RUPEES}`
  }
  basePlanTenure: {
    value: string | number
    period: `${TIME_UNITS}`
  }
  effectivePlanTenure?: {
    value: string | number
    period: `${TIME_UNITS}`
  }
  actualPlanPrice: {
    amount: number
    unit: `${UNITS.RUPEES}`
  }
  specialOffering?: boolean
  signupOffer?: boolean | null
  offerStartTime?: number | null
  offerValidTill?: number | null
  offerType?: ElitePlanOfferType
  tenureDiff?: number
  priority?: number
  /** Optional upgrade path bundled on an eligible plan row */
  upgradePlanDetails?: EliteSubscriptionPlanDetails
  baseSlabPricePercentage?: {
    amount: number
    unit: `${UNITS.PERCENTAGE}`
  }
  effectiveSlabPricePercentage?: {
    amount: number
    unit: `${UNITS.PERCENTAGE}`
  }
}

export type EliteReferralReward = {
  planExtensionMonths?: number | null
  rewardExpiryTime?: number | null
}

/** Family / dashboard mirror of subscription row; fields may be `null` when not applicable. */
export type EliteUserSubscriptionDetails = {
  memberShipStatus: `${ELiteMembershipStatus}`
  membershipFees?: EliteMoney | null
  subscriptionEndTime?: number | null
  /** When present, nested subscription payload for the user row */
  eliteSubscriptionDetail?: EliteSubscriptionDetails | null
  expiredSubscriptionDetail?: EliteSubscriptionDetails | null
  activationExpiryDate?: number | null
  specialOffering?: boolean | null
  signupOffer?: boolean | null
  offerStartTime?: number | null
  offerExpiryTime?: number | null
  appropriatePlanTenure?: ElitePlanTenure | null
  offerType?: ElitePlanOfferType | null
  planId?: string | null
  expired?: boolean | null
  eliteMember?: boolean | null
}

/** When present on the advisory payload, client should surface renewal UI (CTA + optional list price). */
export type EliteOnboardingRenewalPayload = {
  membershipFees?: EliteMoney | null
  cta?: EliteInsightsCta
  status?: `${EliteOnboardingRenewalStatus}`
}

/** Legacy dashboard renewal prop — same shape as advisory `renewal`. */
export type EliteRenewal = EliteOnboardingRenewalPayload

export type PortfolioInsightsLockedStatus = 'NA' | 'GRACE_PERIOD' | 'LOCKED'

export type PortfolioInsightsLockedInfo = {
  gracePeriodEndTimestamp: number | null
  status: PortfolioInsightsLockedStatus
}

export type EliteConsentTextItem = {
  consentText?: string
  textColor?: string | null
  ctaUrl?: string | null
  ctaPayload?: string | null
  ctaType?: string | null
  navigationStyle?: string | null
  ctaMethod?: string | null
  type?: string | null
  textType?: string | null
  clickableText?: boolean
}

//TODO: Add epoch for esign
export type EliteOnboardingStatusResponse = {
  eligiblePlans: EliteOnboardingEligiblePlan[]
  subscriptionDetails: EliteSubscriptionDetails
  eliteLandingPageState: `${EliteLandingPageState}`
  cta?: Record<string, unknown>
  cancelSubscriptionCta?: Record<string, unknown>
  isForceUpdateRequired?: boolean
  numberOfFundsToRebalance?: number
  rebalanceAvailable?: boolean
  missedGains?: number
  consentText?: string
  consentTextList?: EliteConsentTextItem[]
  throughFamilyAccess?: boolean
  primaryUserName?: string
  secondaryUserName?: string
  /** Family context: whether the primary account holder has an active Elite membership. */
  isPrimaryUserElite?: boolean
  /** Family context: whether the viewed (secondary) family member has Elite. */
  isSecondaryUserElite?: boolean
  /** Present when the member should see renewal prompt + renewal CTA */
  renewal?: EliteOnboardingRenewalPayload
  /** Present on some combined / family-aware advisory payloads */
  userSubscriptionDetails?: EliteUserSubscriptionDetails
  portfolioInsightsLockedInfo?: PortfolioInsightsLockedInfo
  eliteInsights?: EliteInsights
  /** Present on `/v2/onboarding/advisory/prime-status` payloads. */
  elitePrimeInsights?: import('./elitePrimeSummary.types').ElitePrimeInsights
  /** Present on `/v2/onboarding/advisory/prime-status` payloads. */
  marketPricingDetails?: import('./elitePrimeSummary.types').ElitePrimeMarketPricingDetails
  /** Present on `/v2/onboarding/advisory/prime-status` — whether the user has Elite Prime. */
  isUserElitePrime?: boolean
  /** Present on `/v2/onboarding/advisory/prime-status` — grant Elite Prime consent before posting the CTA. */
  isPrimeConsent?: boolean
  /** When present, shows referral bonus UI on yearly plans / invite banner. */
  referralReward?: EliteReferralReward | null
  selectedPlanDetails?: {
    membershipFees: {
      amount: number
      unit: `${UNITS.RUPEES}`
    }
    planId: string
    membershipFeesInclGst: {
      amount: number
      unit: `${UNITS.RUPEES}`
    }
    basePlanTenure: {
      value: string
      period: `${TIME_UNITS}`
    }
    effectivePlanTenure: {
      value: string
      period: `${TIME_UNITS}`
    }
    specialOffering: boolean
    offerStartTime: number
    offerExpiryTime: number
  }
}

export type FamilyEliteDetailsResponse = {
  userSubscriptionDetails: EliteUserSubscriptionDetails
  eliteLandingPageState: `${EliteLandingPageState}`
  cta: Record<any, any>
  consentText?: string
  throughFamilyAccess?: boolean
  primaryUserName?: string
  secondaryUserName?: string
}

export interface IconProps {
  fill?: string // Make 'fill' optional
  className?: string // Add if you plan to pass className
  // Add any other common SVG props you might want to pass, e.g.,
  // width?: string | number;
  // height?: string | number;
  // viewBox?: string;
}

export type UserDetailsResponse = {
  name: string
  mobileNumber: string
  emailId: string
  name?: string
}
